// Pure-function policy for web notification grouping.
//
// The `tag` field on a `Notification` (or the `topic` field in a Push
// message) is the web equivalent of iOS's
// `UNNotificationContent.threadIdentifier`. Setting the same `tag` on
// multiple notifications causes the browser to replace the earlier one
// rather than stacking them — a per-thread "latest message" banner, not
// an ever-growing list.
//
// For the notification summary shown inside a grouped banner we emit a
// human-readable string rather than an OS format specifier (the web Push
// API does not have a `%u`-substitution mechanism — the Service Worker
// renders the final string itself using the unread count it holds).
//
// Threat model
// ─────────────
// - The thread identifier is NOT E2EE — it is stored in the browser's
//   push-notification state and is opaque to the server, but the browser
//   sees it. We deliberately do not embed display names ("#general") here:
//   only opaque ids, so the browser's notification store does not leak
//   channel topics.
// - The `1on1:` prefix prevents collision between a 1:1 thread and a
//   channel that happens to share the same id string (which would merge
//   the two buckets visually).
// - When neither id is available we return `null` so the caller can fall
//   back to a generic per-app bucket. We never invent a placeholder id
//   that could lie about which thread a banner belongs to.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/NotificationGroupingPolicy.swift`.

export const NotificationGroupingPolicy = {
  /**
   * Returns the thread identifier to attach to a notification as its `tag`.
   *
   * - `channelId` non-null/non-empty → return it verbatim (server/group channel).
   * - `channelId` null + `peerUserId` non-null/non-empty → `"1on1:<peerUserId>"`.
   * - Both null/empty → `null` (let the browser use its default bucket).
   */
  threadIdentifier(
    channelId: string | null | undefined,
    peerUserId: string | null | undefined,
  ): string | null {
    if (channelId && channelId.length > 0) {
      return channelId;
    }
    if (peerUserId && peerUserId.length > 0) {
      return `1on1:${peerUserId}`;
    }
    return null;
  },

  /**
   * Human-readable summary string for a grouped notification banner.
   *
   * Unlike iOS (which returns a `%u`-bearing format string for OS
   * substitution), the web Service Worker renders the final string
   * directly using the count it holds. This function returns the
   * pre-rendered string.
   *
   * - count === 1 → `"1 new message"`
   * - count >= 2  → `"N new messages"`
   */
  summary(count: number): string {
    return count === 1 ? "1 new message" : `${count} new messages`;
  },
} as const;
